import { useState } from 'react';
import { FlaskConical, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { useStudioStore } from '@/stores/useStudioStore';
import { ProfileForm } from '@/components/studio/ProfileForm';
import { StudioHistory } from '@/components/studio/StudioHistory';
import { CompositionPreview } from '@/components/studio/CompositionPreview';
import { PromptEditor } from '@/components/studio/PromptEditor';
import { StudioResults } from '@/components/studio/StudioResults';

type LeftTab = 'config' | 'prompts';

export function StudioPage() {
  const {
    workbook,
    generating,
    generationError,
    generationResult: _generationResult,
  } = useStudioStore();

  const [leftTab, setLeftTab] = useState<LeftTab>('config');
  const [showComposition, setShowComposition] = useState(false);
  const [showHistory, setShowHistory] = useState(true);

  return (
    <div className="flex h-screen flex-col">
      {/* Page header */}
      <header className="flex items-center gap-3 border-b border-border bg-white px-4 py-3">
        <FlaskConical size={20} className="text-brand" />
        <div>
          <h1 className="font-heading text-lg text-text-primary">Workbook Studio</h1>
          <p className="text-xs text-text-muted">Experimental workbook generation sandbox</p>
        </div>
      </header>

      {/* Main layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left panel — Configuration */}
        <aside className="w-96 shrink-0 overflow-y-auto border-r border-border bg-white">
          {/* Tab bar */}
          <div className="flex border-b border-border">
            <button
              onClick={() => setLeftTab('config')}
              className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
                leftTab === 'config'
                  ? 'border-b-2 border-brand text-brand'
                  : 'text-text-muted hover:text-text-primary'
              }`}
            >
              Profile
            </button>
            <button
              onClick={() => setLeftTab('prompts')}
              className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
                leftTab === 'prompts'
                  ? 'border-b-2 border-brand text-brand'
                  : 'text-text-muted hover:text-text-primary'
              }`}
            >
              Prompts
            </button>
          </div>

          <div className="p-4">
            {leftTab === 'config' ? (
              <>
                <ProfileForm />

                {/* Composition preview (collapsible) */}
                <div className="mt-6">
                  <button
                    onClick={() => setShowComposition(!showComposition)}
                    className="flex w-full items-center gap-1 text-xs font-semibold uppercase tracking-wider text-text-muted hover:text-text-primary"
                  >
                    {showComposition ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    Block Composition
                  </button>
                  {showComposition && (
                    <div className="mt-2">
                      <CompositionPreview />
                    </div>
                  )}
                </div>

                {/* History (collapsible) */}
                <div className="mt-6">
                  <button
                    onClick={() => setShowHistory(!showHistory)}
                    className="flex w-full items-center gap-1 text-xs font-semibold uppercase tracking-wider text-text-muted hover:text-text-primary"
                  >
                    {showHistory ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    History
                  </button>
                  {showHistory && (
                    <div className="mt-2">
                      <StudioHistory />
                    </div>
                  )}
                </div>
              </>
            ) : (
              <PromptEditor />
            )}
          </div>
        </aside>

        {/* Right panel — Results */}
        <div className="flex-1 overflow-hidden">
          {generating ? (
            <div className="flex h-full flex-col items-center justify-center gap-4">
              <Loader2 size={32} className="animate-spin text-brand" />
              <div className="text-center">
                <p className="font-heading text-lg text-text-primary">Generating workbook...</p>
                <p className="mt-1 text-sm text-text-muted">
                  This usually takes 30-60 seconds depending on pace.
                </p>
              </div>
            </div>
          ) : generationError ? (
            <div className="flex h-full items-center justify-center p-6">
              <div className="max-w-md rounded-lg border border-red-200 bg-red-50 p-4">
                <p className="font-medium text-red-700">Generation failed</p>
                <p className="mt-1 text-sm text-red-600">{generationError}</p>
              </div>
            </div>
          ) : workbook ? (
            <StudioResults />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
              <FlaskConical size={48} className="text-text-light" />
              <p className="text-lg font-medium text-text-muted">Configure & Generate</p>
              <p className="max-w-sm text-sm text-text-light">
                Set up a learner profile on the left, then hit Generate to create a workbook. You
                can also load a previous studio workbook from the history.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
